import { useCallback, useEffect, useState } from 'react';
import { useMuvelet } from '../felhasznalo-context.ts';

import {
  api,
  type CegLista,
  type CegReszletek,
  type CegSor,
  type EtteremLista,
  type EtteremSor,
} from '../api.ts';
import { ft } from '../formazas.ts';
import { letolt } from '../letoltes.ts';
import { Mezo } from './Mezo.tsx';
import { UjEtterem } from './UjEtterem.tsx';

interface TelephelyKapcsolasValasz {
  etterem_nev: string;
  szamla_db: number;
  megszunt_ceg: string | null;
  ceg: CegReszletek;
}

/**
 * „+ telephely" → meglévő partner. (A megrendelő kérése, 2026-09-14)
 *
 * Keresés a teljes partnertörzsben; a találat egy kattintással a cég
 * telephelye lesz, a számláival együtt. Ha a partner eddig egy több
 * telephelyes cégnél volt, a gomb egyszer rákérdez.
 */
function PartnerKapcsolo({
  ceg,
  onKesz,
}: {
  ceg: CegSor;
  onKesz: (eredmeny: TelephelyKapcsolasValasz) => void;
}) {
  const [kereses, setKereses] = useState('');
  const [talalatok, setTalalatok] = useState<EtteremSor[]>([]);
  const [cegNevek, setCegNevek] = useState<Map<number, CegSor>>(new Map());
  const [megerosit, setMegerosit] = useState<number | null>(null);
  const [ment, setMent] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);

  useEffect(() => {
    const q = kereses.trim();
    setMegerosit(null);
    if (q.length < 2) {
      setTalalatok([]);
      return;
    }
    const idozito = setTimeout(async () => {
      try {
        const [partnerek, cegek] = await Promise.all([
          api.get<EtteremLista>(`/api/etterem?q=${encodeURIComponent(q)}&limit=15&csak_aktiv=1`),
          cegNevek.size ? Promise.resolve(null) : api.get<CegLista>('/api/ceg'),
        ]);
        setTalalatok(partnerek.sorok);
        if (cegek) setCegNevek(new Map(cegek.sorok.map((x) => [x.id, x])));
      } catch {
        setTalalatok([]);
      }
    }, 200);
    return () => clearTimeout(idozito);
  }, [kereses, cegNevek.size]);

  async function kapcsol(e: EtteremSor) {
    const jelenlegi = e.ceg_id !== null ? cegNevek.get(e.ceg_id) : undefined;
    if (jelenlegi && jelenlegi.telephelyek > 1 && megerosit !== e.id) {
      setMegerosit(e.id);
      return;
    }
    setMent(true);
    setHiba(null);
    try {
      onKesz(
        await api.post<TelephelyKapcsolasValasz>(`/api/ceg/${ceg.id}/telephely`, {
          etterem_id: e.id,
        }),
      );
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az összekötés nem sikerült.');
    } finally {
      setMent(false);
    }
  }

  return (
    <div className="tdet">
      <h4>Meglévő partner telephelyként — {ceg.nev}</h4>
      <input
        className="qi"
        style={{ width: 340 }}
        autoFocus
        autoComplete="off"
        placeholder="partner keresése név, kód vagy cím alapján…"
        value={kereses}
        onChange={(e) => setKereses(e.target.value)}
      />
      {hiba && (
        <div className="uzenet bad" style={{ marginTop: 8 }}>
          {hiba}
        </div>
      )}
      {talalatok.length > 0 && (
        <div className="matrix" style={{ marginTop: 8, maxHeight: 300, overflow: 'auto' }}>
          <table className="tt2">
            <thead>
              <tr>
                <th>Kód</th>
                <th>Partner</th>
                <th>Cím</th>
                <th>Jelenlegi cég</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {talalatok.map((e) => {
                const jelenlegi = e.ceg_id !== null ? cegNevek.get(e.ceg_id) : undefined;
                const marItt = e.ceg_id === ceg.id;
                return (
                  <tr key={e.id}>
                    <td style={{ padding: '6px 10px' }}>{e.kod ?? '—'}</td>
                    <td className="nm3">
                      <b>{e.nev}</b>
                    </td>
                    <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                      {[e.irsz, e.cim].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                      {jelenlegi ? (
                        <>
                          {jelenlegi.nev}
                          <small style={{ display: 'block', color: 'var(--muted)' }}>
                            {jelenlegi.adoszam ?? 'adószám nélkül'} · {jelenlegi.telephelyek}{' '}
                            telephely
                          </small>
                        </>
                      ) : (
                        e.cegnev ?? '—'
                      )}
                    </td>
                    <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>
                      {marItt ? (
                        <span className="hint">már itt van</span>
                      ) : (
                        <button
                          className={`btn sm${megerosit === e.id ? ' pri' : ''}`}
                          disabled={ment}
                          title={
                            megerosit === e.id
                              ? `Kiveszi a(z) ${jelenlegi?.nev ?? ''} cégből`
                              : undefined
                          }
                          onClick={() => void kapcsol(e)}
                        >
                          {megerosit === e.id ? 'Igen, áthelyezem' : 'Hozzákapcsolás'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {kereses.trim().length >= 2 && talalatok.length === 0 && (
        <div className="hint" style={{ marginTop: 8 }}>
          Nincs ilyen partner. Az „Új partner felvétele” gombbal most felveheted.
        </div>
      )}
    </div>
  );
}

/**
 * Cégek. (Megbízás 2.0 / 5.8)
 *
 * A cég a partnerek fölötti szint: egy cég több telephelyet is vihet. Ez a
 * pénzügy miatt kell — **egy utalás több telephely számláját is kifizetheti**,
 * és a fizetési felszólítás is a cégnek megy, nem az egyes éttermeknek.
 *
 * A cégeket nem kézzel vittük fel: a 2.0-ra átálláskor a program állította
 * elő őket a már rögzített adószámokból.
 */

const SZURO_SORREND = [
  'mind',
  'tobbtelephely',
  'egytelephely',
  'vannyitott',
  'lejart',
  'nincsado',
  'nincsszekhely',
  'inaktiv',
] as const;

/** Oszlopok: kulcs, felirat, szám-e, és melyik irányba rendez elsőre. */
const OSZLOPOK = [
  { kulcs: 'nev', cimke: 'Cégnév', szam: false },
  { kulcs: 'adoszam', cimke: 'Adószám', szam: false },
  { kulcs: 'szekhely', cimke: 'Székhely', szam: false },
  { kulcs: 'telephelyek', cimke: 'Telephely', szam: true },
  { kulcs: 'nyitott_db', cimke: 'Nyitott számla', szam: true },
  { kulcs: 'nyitott_osszeg', cimke: 'Összeg', szam: true },
] as const;

export function Cegek({ fejlec }: { fejlec?: React.ReactNode }) {
  const letolthet = useMuvelet('export_torzs');
  const [kereses, setKereses] = useState('');
  const [keresesKesleltetve, setKeresesKesleltetve] = useState('');
  const [szuro, setSzuro] = useState('mind');
  const [rendezes, setRendezes] = useState<string>('nev');
  const [irany, setIrany] = useState<'novekvo' | 'csokkeno'>('novekvo');
  const [lista, setLista] = useState<CegLista | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [nyitott, setNyitott] = useState<CegReszletek | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setKeresesKesleltetve(kereses), 220);
    return () => clearTimeout(t);
  }, [kereses]);

  const betolt = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (keresesKesleltetve.trim()) p.set('q', keresesKesleltetve.trim());
      if (szuro !== 'mind') p.set('szuro', szuro);
      p.set('rendezes', rendezes);
      p.set('irany', irany);
      setLista(await api.get<CegLista>(`/api/ceg?${p}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a cégeket.');
    }
  }, [keresesKesleltetve, szuro, rendezes, irany]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /** Oszlopfejlécre kattintva rendez. Ugyanarra kattintva irányt vált. */
  function rendez(kulcs: string, szam: boolean) {
    if (rendezes === kulcs) {
      setIrany((e) => (e === 'novekvo' ? 'csokkeno' : 'novekvo'));
    } else {
      setRendezes(kulcs);
      // A számoknál a legnagyobb az érdekes, a szövegeknél az ábécé eleje.
      setIrany(szam ? 'csokkeno' : 'novekvo');
    }
  }

  const ment = useCallback(
    async (id: number, mezo: string, ertek: unknown) => {
      const frissitett = await api.patch<CegSor>(`/api/ceg/${id}/mezo/${mezo}`, { ertek });
      /*
       * Az aktív jelölő átállítása után a sor **kikerül** a listából (vagy
       * bejön az Inaktív szűrőn), ezért újratöltünk. A többi mezőnél elég a
       * sort cserélni. (2026-10-14)
       */
      if (mezo === 'aktiv') {
        await betolt();
        return;
      }
      setLista((elozo) =>
        elozo
          ? { ...elozo, sorok: elozo.sorok.map((c) => (c.id === id ? frissitett : c)) }
          : elozo,
      );
    },
    [betolt],
  );

  async function reszletek(id: number) {
    try {
      setNyitott(await api.get<CegReszletek>(`/api/ceg/${id}`));
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült megnyitni a céget.');
    }
  }

  const sorok = lista?.sorok ?? [];
  const o = lista?.osszesites;
  const nyil = (kulcs: string) =>
    rendezes === kulcs ? (irany === 'csokkeno' ? ' ▾' : ' ▴') : ' ⇅';

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Partnerek</span>
        {fejlec}
        <input
          className="qi"
          style={{ width: 270, fontSize: 12.5 }}
          placeholder="keresés cégnév, adószám, székhely, telephely…"
          value={kereses}
          onChange={(e) => setKereses(e.target.value)}
        />
        <div className="stats">
          <div>
            Cég
            <strong>{ft(lista?.osszes ?? 0)}</strong>
          </div>
          <div>
            Szűrve
            <strong>{ft(lista?.talalat ?? 0)}</strong>
          </div>
        </div>
      </div>

      <div className="cards">
        <div className="card">
          <em>Cég összesen</em>
          <b>{ft(o?.ceg ?? 0)}</b>
          <span>{o ? `${ft(o.etterem)} étteremből` : ''}</span>
        </div>
        <div className="card">
          <em>Több telephelyes</em>
          <b>{ft(o?.tobb_telephelyes ?? 0)}</b>
          <span>{o ? `${ft(o.tobb_telephelyes_etterem)} étterem` : ''}</span>
        </div>
        <div className={`card${(o?.adoszam_nelkul ?? 0) > 0 ? ' warn2' : ''}`}>
          <em>Adószám nélkül</em>
          <b>{ft(o?.adoszam_nelkul ?? 0)}</b>
          <span>összevonás nem lehetséges</span>
        </div>
        <div className="card">
          <em>Nyitott számla</em>
          <b>{ft(o?.nyitott_osszeg ?? 0)}</b>
          <span>Ft</span>
        </div>
      </div>

      <div className="fchips">
        {SZURO_SORREND.map((k) => (
          <button
            key={k}
            className={`fchip${szuro === k ? ' on' : ''}`}
            onClick={() => setSzuro(k)}
          >
            {lista?.szuro_nevek[k] ?? k}
            <b>{ft(lista?.szuro_darabok[k] ?? 0)}</b>
          </button>
        ))}
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}

      <div className="actbar">
        {letolthet && (
          <button
            className="btn sm"
            /*
              A letöltés azt adja, ami a képernyőn van: a szűrővel és a
              kereséssel együtt. Enélkül a „Székhely hiányzik" szűrőre
              kattintva is az egész cégtörzs jött le. (2026-10-21)
            */
            onClick={() => {
              const p = new URLSearchParams();
              if (szuro !== 'mind') p.set('szuro', szuro);
              if (keresesKesleltetve.trim()) p.set('q', keresesKesleltetve.trim());
              letolt(`/api/export/cegek.xlsx?${p}`);
            }}
            title="A most látható lista letöltése Excelbe"
          >
            Excel
          </button>
        )}
        <span className="hint">
          A cégek adószám alapján állnak össze. Egy cég több telephelyet vihet; a számla és
          az utalás cégszinten értendő.
        </span>
      </div>

      {nyitott && (
        <CegReszletNezet
          key={nyitott.ceg.id}
          adat={nyitott}
          onBezar={() => setNyitott(null)}
          onFrissit={(uj) => {
            setNyitott(uj);
            void betolt();
          }}
        />
      )}

      <div className="matrix" style={{ maxHeight: 600, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              {OSZLOPOK.map((osz) => (
                <th
                  key={osz.kulcs}
                  className={`hsort${osz.szam ? ' n' : ''}`}
                  onClick={() => rendez(osz.kulcs, osz.szam)}
                  style={{ cursor: 'pointer' }}
                >
                  {osz.cimke}
                  <span className="sortjel">{nyil(osz.kulcs)}</span>
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {sorok.map((c) => (
              <tr key={c.id} className={c.adoszam ? undefined : 'nul'}>
                <td className="nm3" onClick={() => void reszletek(c.id)} title="Részletek">
                  <b>{c.nev}</b>
                  {c.telephelyek > 1 && (
                    <small>
                      {c.telephely_nevek.slice(0, 3).join(' · ')}
                      {c.telephelyek > 3 ? ' …' : ''}
                    </small>
                  )}
                </td>
                <td>
                  <Mezo
                    ertek={c.adoszam}
                    onMent={(v) => ment(c.id, 'adoszam', v)}
                    helykitolto="nincs"
                    cim="Alakja: 12345678-2-42"
                  />
                </td>
                <td>
                  <Mezo ertek={c.szekhely} onMent={(v) => ment(c.id, 'szekhely', v)} />
                </td>
                <td
                  className="calc2"
                  style={{
                    color: c.telephelyek > 1 ? 'var(--blue)' : 'var(--muted)',
                    fontWeight: c.telephelyek > 1 ? 600 : 400,
                  }}
                >
                  {c.telephelyek}
                </td>
                <td className={`calc2${c.lejart_db > 0 ? ' bad2' : ''}`}>
                  {c.nyitott_db || '·'}
                  {c.lejart_db > 0 && (
                    <div style={{ fontSize: 10 }}>({c.lejart_db} lejárt)</div>
                  )}
                </td>
                <td className="calc2" style={{ color: 'var(--ink)', fontWeight: 600 }}>
                  {c.nyitott_db ? ft(c.nyitott_osszeg) : '·'}
                </td>
                {/*
                  * Inaktiválás: nem törlés. A számlái és a felszólítási
                  * előzménye megmarad, de a cég eltűnik a listákból és a
                  * választókból — csak az „Inaktív" szűrőn jön elő.
                  * (2026-10-14, a megrendelő kérése)
                  */}
                <td className="act" style={{ whiteSpace: 'nowrap' }}>
                  {c.aktiv ? (
                    <button
                      className="btn sm torlo"
                      title="A cég eltűnik a listákból és a választókból. Az adatai megmaradnak."
                      onClick={() => void ment(c.id, 'aktiv', false)}
                    >
                      Inaktiválás
                    </button>
                  ) : (
                    <button className="btn sm" onClick={() => void ment(c.id, 'aktiv', true)}>
                      Visszakapcsolás
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  Nincs találat.
                </td>
              </tr>
            )}
          </tbody>
          {sorok.length > 0 && lista && (
            <tfoot>
              <tr>
                <td className="dcol">{ft(lista.talalat)} cég</td>
                <td></td>
                <td></td>
                <td className="calc2">{ft(lista.labjegyzet.telephelyek)}</td>
                <td className="calc2">{lista.labjegyzet.nyitott_db || '·'}</td>
                <td className="calc2">
                  {lista.labjegyzet.nyitott_db ? ft(lista.labjegyzet.nyitott_osszeg) : '·'}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function CegReszletNezet({
  adat,
  onBezar,
  onFrissit,
}: {
  adat: CegReszletek;
  onBezar: () => void;
  /** Telephely hozzáadása után a cég friss adataival hívódik. */
  onFrissit: (uj: CegReszletek) => void;
}) {
  const c = adat.ceg;
  const [mod, setMod] = useState<null | 'meglevo' | 'uj'>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  /*
   * Telephely kivétele és áthelyezése. (2026-10-09, a megrendelő kérése)
   *
   * Cége és partner külön identitás — ha egy partner tévedésből került egy
   * cég alá, innen kell tudni kivenni vagy átmozgatni. A kivétel a saját
   * cégét adja neki, az áthelyezés egy kiválasztott másik céget.
   */
  const [athelyez, setAthelyez] = useState<number | null>(null);
  const [celCeg, setCelCeg] = useState('');
  /**
   * Cégváltás: a partner átveszi a cég nevét, adószámát, székhelyét.
   * (2026-10-14, a megrendelő jelzése)
   *
   * Enélkül az eltérő adószám megállította az áthelyezést, és a cégváltást
   * nem lehetett rögzíteni.
   */
  const [cegAdatok, setCegAdatok] = useState(true);
  const [cegek, setCegek] = useState<Array<{ id: number; nev: string }>>([]);

  async function cegeketBetolt() {
    if (cegek.length > 0) return;
    const v = await api.get<{ sorok: Array<{ id: number; nev: string }> }>(
      '/api/ceg?szuro=mind&limit=2000',
    );
    setCegek(v.sorok.filter((x) => x.id !== c.id));
  }

  async function kivesz(etterem_id: number, nev: string) {
    try {
      const v = await api.post<{ ceg_nev: string; szamla_db: number; ceg: CegReszletek }>(
        `/api/ceg/${c.id}/telephely-kivetel`,
        { etterem_id },
      );
      setHiba(null);
      setUzenet(
        `${nev} kikerült a(z) ${c.nev} alól — mostantól nincs cég alatt.` +
          (v.szamla_db > 0
            ? ` A ${v.szamla_db} korábbi számlája ott marad, ahol volt: azok a kiállításkori állapotot őrzik.`
            : '') +
          ' Ha másik céghez tartozik, használd az Áthelyezést.',
      );
      onFrissit(v.ceg);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A kivétel nem sikerült.');
    }
  }

  async function athelyezes(etterem_id: number, nev: string) {
    if (!celCeg) return;
    try {
      const v = await api.post<{ szamla_db: number }>(`/api/ceg/${celCeg}/telephely`, {
        etterem_id,
        ceg_adatok: cegAdatok,
      });
      const cel = cegek.find((x) => String(x.id) === celCeg)?.nev ?? 'a másik cég';
      setHiba(null);
      setAthelyez(null);
      setCelCeg('');
      setUzenet(
        `${nev} átkerült a(z) ${cel} alá` +
          (v.szamla_db > 0 ? `, ${v.szamla_db} számlájával együtt` : '') +
          (cegAdatok ? ' — a cégnevet, adószámot és székhelyet is átvette.' : '.'),
      );
      onFrissit(await api.get<CegReszletek>(`/api/ceg/${c.id}`));
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az áthelyezés nem sikerült.');
    }
  }

  async function ujUtan() {
    setMod(null);
    setUzenet('Az új telephely felvéve, és a céghez kötve.');
    onFrissit(await api.get<CegReszletek>(`/api/ceg/${c.id}`));
  }

  return (
    <>
      <div className="tdet">
        <h4>{c.nev}</h4>
        <div className="g4">
          <div>
            <label>Adószám</label>
            <div className="rdv">{c.adoszam ?? '—'}</div>
          </div>
          <div>
            <label>Székhely</label>
            <div className="rdv">{c.szekhely ?? '—'}</div>
          </div>
          <div>
            <label>Telephely</label>
            <div className="rdv">{c.telephelyek} db</div>
          </div>
          <div>
            <label>Nyitott</label>
            <div className="rdv">
              {c.nyitott_db} számla · {ft(c.nyitott_osszeg)} Ft
            </div>
          </div>
        </div>
        <div className="btns">
          <button
            className={`btn sm${mod ? '' : ' pri'}`}
            onClick={() => {
              setMod(mod ? null : 'meglevo');
              setUzenet(null);
            }}
          >
            {mod ? '× Mégsem' : '+ telephely'}
          </button>
          <button className="btn sm" onClick={onBezar}>
            Bezárás
          </button>
        </div>
      </div>

      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}
      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}

      {mod && (
        <div className="fchips" style={{ marginBottom: 8 }}>
          <button
            className={`fchip${mod === 'meglevo' ? ' on' : ''}`}
            onClick={() => setMod('meglevo')}
          >
            Meglévő partner a rendszerből
          </button>
          <button className={`fchip${mod === 'uj' ? ' on' : ''}`} onClick={() => setMod('uj')}>
            Új partner felvétele
          </button>
        </div>
      )}
      {mod === 'meglevo' && (
        <PartnerKapcsolo
          ceg={c}
          onKesz={(eredmeny) => {
            setMod(null);
            setUzenet(
              `A(z) „${eredmeny.etterem_nev}” mostantól a(z) ${c.nev} telephelye` +
                (eredmeny.szamla_db ? `, ${eredmeny.szamla_db} számlájával együtt` : '') +
                '.' +
                (eredmeny.megszunt_ceg
                  ? ` A korábbi, üresen maradt „${eredmeny.megszunt_ceg}” cég megszűnt.`
                  : ''),
            );
            onFrissit(eredmeny.ceg);
          }}
        />
      )}
      {mod === 'uj' && (
        <UjEtterem ceg={c} onKesz={() => void ujUtan()} onMegse={() => setMod(null)} />
      )}

      <p className="lbl" style={{ marginTop: 12 }}>
        Telephelyek
      </p>
      <div className="matrix" style={{ marginBottom: 14 }}>
        <table className="tt2">
          <thead>
            <tr>
              <th>Telephely</th>
              <th>Cím</th>
              <th className="n">Ár</th>
              <th>Fizetés</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {adat.telephelyek.map((t) => (
              <tr key={t.id} className={t.aktiv ? undefined : 'nul'}>
                <td className="nm3">
                  <b>{t.nev}</b>
                  {/*
                    Az inaktív telephely a cég számában már nem szerepel —
                    itt viszont látszik, hogy vissza lehessen kapcsolni.
                    (2026-11-03)
                  */}
                  {!t.aktiv && (
                    <span className="elojel" title="Inaktív: a cég telephelyei közt nem számít">
                      inaktív
                    </span>
                  )}
                </td>
                <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                  {[t.irsz, t.cim, t.helyseg].filter(Boolean).join(' ') || '—'}
                </td>
                <td className="calc2">
                  {t.brutto_ar ? (
                    ft(t.brutto_ar)
                  ) : (
                    <span className="only">{t.csak_gyujtes ? 'gyűjtés' : 'nincs ár'}</span>
                  )}
                </td>
                <td style={{ padding: '6px 10px' }}>{t.fizetesi_mod ?? 'Készpénz'}</td>
                <td className="act" style={{ whiteSpace: 'nowrap' }}>
                  {athelyez === t.id ? (
                    <>
                      <select value={celCeg} onChange={(e) => setCelCeg(e.target.value)}>
                        <option value="">melyik céghez…</option>
                        {cegek.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.nev}
                          </option>
                        ))}
                      </select>{' '}
                      <label
                        style={{ fontSize: 11, color: 'var(--muted)' }}
                        title="Cégváltásnál ez kell: a partnerre az új cég neve, adószáma és székhelye kerül. Enélkül az eltérő adószám megállítja az áthelyezést."
                      >
                        <input
                          type="checkbox"
                          checked={cegAdatok}
                          onChange={(e) => setCegAdatok(e.target.checked)}
                        />{' '}
                        cégváltás (átveszi a cég adatait)
                      </label>{' '}
                      <button
                        className="btn sm gold"
                        disabled={!celCeg}
                        onClick={() => void athelyezes(t.id, t.nev)}
                      >
                        Áthelyezés
                      </button>{' '}
                      <button className="btn sm" onClick={() => setAthelyez(null)}>
                        Mégsem
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn sm"
                        title="Átmozgatás egy másik céghez — a számlái is vele mennek"
                        onClick={() => {
                          setAthelyez(t.id);
                          void cegeketBetolt();
                        }}
                      >
                        Áthelyezés
                      </button>{' '}
                      <button
                        className="btn sm torlo"
                        title="Kivétel a cég alól: a partner cég nélkül marad. A már kiállított számlái ott maradnak, ahol vannak."
                        onClick={() => void kivesz(t.id, t.nev)}
                      >
                        Kivétel
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adat.szamlak.length > 0 ? (
        <>
          <p className="lbl">Számlák</p>
          <div className="matrix" style={{ maxHeight: 320, overflow: 'auto', marginBottom: 14 }}>
            <table className="tt2">
              <thead>
                <tr>
                  <th>Bizonylat</th>
                  <th>Telephely</th>
                  <th className="n">Kelt</th>
                  <th className="n">Határidő</th>
                  <th className="n">Bruttó</th>
                  <th>Állapot</th>
                </tr>
              </thead>
              <tbody>
                {adat.szamlak.map((s) => (
                  <tr key={s.id}>
                    <td className="nm3">
                      <b>{s.bizonylatszam}</b>
                    </td>
                    <td style={{ padding: '6px 10px', fontSize: 11.5 }}>
                      {s.telephely_nev ?? '—'}
                    </td>
                    <td className="calc2">{s.kelt ?? '—'}</td>
                    <td className="calc2">{s.fizetesi_hatarido ?? '—'}</td>
                    <td className="calc2">{ft(s.brutto)}</td>
                    <td style={{ padding: '6px 10px' }}>
                      {s.storno || s.stornozva ? (
                        <span className="only">stornó</span>
                      ) : s.jovairas_datum ? (
                        <span style={{ color: 'var(--ok)' }}>kifizetve {s.jovairas_datum}</span>
                      ) : s.keses > 0 ? (
                        <span style={{ color: 'var(--bad)', fontWeight: 600 }}>
                          {s.keses} napja lejárt
                        </span>
                      ) : (
                        <span style={{ color: 'var(--flag)' }}>nyitott</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="uzenet flag" style={{ marginBottom: 14 }}>
          Ehhez a céghez még nincs számla rögzítve. A számlák az 5. blokkban kerülnek be.
        </div>
      )}
    </>
  );
}
